/**
 * La plantilla de TODO lo que se imprime en hoja carta (2026-09-29).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  CÓMO LA PIDIÓ EL DUEÑO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Mostró un recibo de otro sistema como modelo («así debe ser la plantilla
 * para la nota de entrega, factura, cotizaciones y todo lo que sea hoja
 * carta») y dijo lo que estaba mal en la primera nota: «no usa toda la hoja…
 * tienes que respetar el encabezado y la parte de abajo».
 *
 *   ┌──────────────────────────────────────────────────────┐
 *   │ [logo]                          NOMBRE DEL NEGOCIO   │  ← encabezado
 *   │                          dirección de la sucursal    │
 *   │                                  Telf. · RIF         │
 *   │ ┌ TÍTULO N.° ────────────────────────── FECHA ─────┐ │  ← recuadro
 *   │ │ ETIQUETA: valor            ETIQUETA: valor       │ │
 *   │ └──────────────────────────────────────────────────┘ │
 *   │ ┌ CABECERA SOMBREADA ──────────────────────────────┐ │  ← tabla
 *   │ │ filas…                                            │ │
 *   │ ┌ TOTAL DE PRODUCTOS: n        TOTAL: …  ─────────┐ │  ← totales
 *   │ ┌ Observaciones │ …                               ┐ │
 *   │                                                      │
 *   │            (la hoja entera, aunque sea un producto)  │
 *   │   firmas                                             │
 *   │ softfi.online · Página 1 de 2   DOCUMENTO NO FISCAL  │  ← pie, abajo
 *   └──────────────────────────────────────────────────────┘
 *
 * Cada hoja repite el encabezado, el recuadro y el pie; los totales, las
 * observaciones y las firmas van en la última. El logo sale si el negocio lo
 * tiene (`logo_url`); si no, el nombre ocupa su lugar.
 *
 * ⚠️ EN UNA SUCURSAL QUE NO ES LA PRINCIPAL, EL ENCABEZADO VA EN TRES PARTES
 * (lo pidió el dueño, 2026-10-01, con un recibo anotado a mano):
 *
 *   │ DOMICILIO FISCAL            [logo]            Sucursal La Fría │
 *   │ Razón social                                  su dirección     │
 *   │ dirección · Telf. · RIF                       Telf.            │
 *
 * A la izquierda, la sede principal (los datos del negocio, que son los
 * fiscales); al centro, el logo; a la derecha, la sucursal donde se hizo. En
 * la principal, el de siempre. Ver `Emisor.domicilioFiscal`.
 *
 * ⚠️ UN DOCUMENTO CON NOMBRE PROPIO lo lleva centrado y grande entre el
 * encabezado y el recuadro (`documento`, 2026-10-01): «GUÍA DE DESPACHO», y en
 * el recuadro «TRASLADO DE ALMACÉN N.° …». Así lo trajo el dueño anotado.
 *
 * Las hojas se reparten MIDIENDO cada fila (un nombre largo ocupa dos líneas)
 * y se imprimen con `abrirHojasCarta` (en su pestaña), que no toca la letra: cada hoja de
 * la pantalla es una hoja de la impresora. «Enviar» las manda como PDF con
 * `hojasCartaComoPdf`, que copia lo que se ve. La tinta es fija, sin tema: es
 * un papel.
 */

import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { Maximize2, Minimize2 } from "lucide-react";
import { abrirHojasCarta, CLASE_HOJA_CARTA, HOJA_CARTA_PX } from "@/lib/imprimirHoja";
import type { Tenant } from "@/lib/types";

// ── La tinta ─────────────────────────────────────────────────────────────────
export const TINTA = "#111827";
export const TENUE = "#4b5563";
export const LINEA = "#d1d5db";
export const LINEA_FUERTE = "#374151";
export const ROJO = "#b91c1c";
/** El sombreado de la cabecera de la tabla: se ve en cualquier impresora. */
const SOMBRA = "#e6edf2";

// ── La hoja ──────────────────────────────────────────────────────────────────
const MARGEN_X = 44;
const MARGEN_Y = 40;
const ANCHO_UTIL = HOJA_CARTA_PX.ancho - 2 * MARGEN_X;
// Lo que cabe de alto, con holgura: la letra de la impresora no mide al píxel
// lo mismo que la pantalla, y quedarse corto corta la última fila.
const ALTO_UTIL = HOJA_CARTA_PX.alto - 2 * MARGEN_Y - 20;
/** El aire entre una hoja y la siguiente, en la pantalla. */
const SEPARACION = 16;

const LETRA: CSSProperties = {
  color: TINTA,
  fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
  fontSize: "11.5px",
  lineHeight: 1.4,
};

export const ROTULO: CSSProperties = {
  fontSize: "9.5px",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: TENUE,
};

// =============================================================================
//  Lo que recibe
// =============================================================================

/** Quién emite el papel: el negocio, y la sucursal con su dirección y teléfono. */
export type Emisor = {
  negocio: string;
  /** La razón social, si es distinta del nombre: «Inversiones El Corte, C.A.». */
  razonSocial?: string | null;
  logo?: string | null;
  rif?: string | null;
  sucursal?: string | null;
  direccion?: string | null;
  telefono?: string | null;
  /**
   * El domicilio fiscal (la sede principal), solo cuando el papel es de OTRA
   * sucursal: el encabezado pasa a tres partes. Ver `domicilioFiscalDe`.
   */
  domicilioFiscal?: DomicilioFiscal | null;
};

/** Los datos fiscales del negocio: los de la sede principal («Datos de mi negocio»). */
export type DomicilioFiscal = {
  nombre: string;
  direccion?: string | null;
  telefono?: string | null;
  rif?: string | null;
};

/**
 * El domicilio fiscal para el encabezado, o `null` si el papel es de la sede
 * principal (o no se sabe: entonces, el encabezado de siempre).
 */
export function domicilioFiscalDe(
  negocio: Tenant | undefined,
  esPrincipal: boolean | undefined,
): DomicilioFiscal | null {
  if (esPrincipal !== false || !negocio) return null;
  return {
    nombre: negocio.legal_name || negocio.name,
    direccion: negocio.address,
    telefono: negocio.phone,
    rif: negocio.tax_id,
  };
}

export type DatoDelDocumento = {
  etiqueta: string;
  valor: ReactNode;
  /** A lo ancho del recuadro, no en media columna: un texto largo como el motivo. */
  completo?: boolean;
};

export type ColumnaDeHoja = {
  titulo: string;
  /** En píxeles. Sin ancho, la columna se queda con lo que sobre. */
  ancho?: number;
  derecha?: boolean;
};

export type FilaDeHoja = { id: string; celdas: ReactNode[] };

export type TotalDeHoja = { etiqueta: string; valor: ReactNode; fuerte?: boolean };

export type Firma = { etiqueta: string; nombre?: string | null };

export type HojaCartaProps = {
  /** El elemento que se imprime: `imprimirHojasCarta(id)`. */
  id: string;
  /** El rótulo del número en el recuadro: «Nota de entrega», «Traslado de almacén». */
  titulo: string;
  /**
   * El nombre del documento, centrado y grande sobre el recuadro: «Guía de
   * despacho». Sin él, el título del recuadro es el nombre.
   */
  documento?: string;
  numero: string;
  fecha: ReactNode;
  emisor: Emisor;
  /** El recuadro de arriba, en dos columnas: cliente, sucursales… */
  datos: DatoDelDocumento[];
  /** Un aviso a lo ancho bajo el recuadro, solo en la primera hoja: «ANULADO». */
  aviso?: ReactNode;
  columnas: ColumnaDeHoja[];
  filas: FilaDeHoja[];
  /** A la izquierda del recuadro de totales: «TOTAL DE PRODUCTOS: 3». */
  resumen: ReactNode;
  totales: TotalDeHoja[];
  observaciones?: string | null;
  /** Una nota chica sobre las observaciones: «Costos en USD…». */
  aclaracion?: ReactNode;
  /** Centrado y en negrita bajo las observaciones: «¡Gracias por su compra!». */
  despedida?: ReactNode;
  firmas?: Firma[];
  /** Abajo a la derecha, en cada hoja. */
  leyenda?: string;
  /** El botón «Ampliar». Sin él en una columna angosta, donde no amplía nada. */
  ampliable?: boolean;
};

// =============================================================================
//  El reparto en hojas
// =============================================================================

/**
 * Qué filas van en cada hoja, midiendo lo que ocupa cada una.
 *
 *   · `primera` / `siguientes`: lo que queda para filas en cada hoja después
 *     del encabezado, el recuadro, la cabecera de la tabla y el pie.
 *   · `final`: los totales, las observaciones y las firmas. Van en la última
 *     hoja; si no caben, pasan a otra llevándose un par de filas, para que
 *     las firmas nunca queden solas en una hoja.
 */
export function repartir(
  altos: number[],
  cabe: { primera: number; siguientes: number; final: number },
): number[][] {
  const hojas: number[][] = [[]];
  let libre = cabe.primera;
  altos.forEach((alto, i) => {
    if (alto > libre && hojas[hojas.length - 1]!.length > 0) {
      hojas.push([]);
      libre = cabe.siguientes;
    }
    hojas[hojas.length - 1]!.push(i);
    libre -= alto;
  });
  if (libre < cabe.final) {
    const ultima = hojas[hojas.length - 1]!;
    const llevar = Math.min(2, Math.max(0, ultima.length - 1));
    hojas.push(ultima.splice(ultima.length - llevar, llevar));
  }
  return hojas;
}

/** «Norte» → «Sucursal Norte»; «Sede principal» se queda como está. */
function rotularSucursal(nombre: string): string {
  return /^(sucursal|sede|local|tienda)\b/i.test(nombre.trim()) ? nombre : `Sucursal ${nombre}`;
}

// =============================================================================
//  La ventana de un documento
// =============================================================================

/**
 * Los datos a la izquierda y la hoja a la derecha, como la ventana de una
 * venta (lo pidió el dueño, 2026-09-29: la hoja sola en un fondo gris se veía
 * «incómoda, apretada»). En el celular, uno debajo del otro: los datos
 * primero.
 */
export function ConDatosAlLado({ datos, children }: { datos: ReactNode; children: ReactNode }) {
  return (
    <div className="lg:grid lg:grid-cols-[minmax(260px,320px)_minmax(0,1fr)] lg:items-start lg:gap-6">
      <div className="space-y-3">{datos}</div>
      <div className="mt-4 min-w-0 lg:mt-0">{children}</div>
    </div>
  );
}

export function BloqueDeDatos({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <div className="rounded-[var(--radius-card)] border border-line bg-surface p-3">
      <p className="rotulo mb-1.5">{titulo}</p>
      {children}
    </div>
  );
}

export function DatoDeBloque({
  etiqueta,
  valor,
  fuerte = false,
}: {
  etiqueta: string;
  valor: ReactNode;
  fuerte?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-0.5 text-sm">
      <span className="shrink-0 text-muted">{etiqueta}</span>
      <span className={fuerte ? "min-w-0 text-right font-semibold text-ink" : "min-w-0 text-right text-ink"}>
        {valor}
      </span>
    </div>
  );
}

/** El rótulo chico de cada lado: «DOMICILIO FISCAL», «SUCURSAL». */
const ROTULO_CHICO: CSSProperties = {
  fontSize: "8.5px",
  fontWeight: 700,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: TENUE,
  marginBottom: "2px",
};

/**
 * El encabezado de una sucursal que no es la principal: domicilio fiscal a la
 * izquierda, logo al centro, la sucursal a la derecha. Ver el diagrama de arriba.
 */
function EncabezadoDeSucursal({ emisor, fiscal }: { emisor: Emisor; fiscal: DomicilioFiscal }) {
  const texto: CSSProperties = { color: TENUE, fontSize: "10px", lineHeight: 1.45 };
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) auto minmax(0, 1fr)",
        alignItems: "start",
        gap: "20px",
      }}
    >
      <div style={texto}>
        <div style={ROTULO_CHICO}>Domicilio fiscal</div>
        <div style={{ fontSize: "12.5px", fontWeight: 700, color: TINTA }}>{fiscal.nombre}</div>
        {fiscal.direccion && <div>{fiscal.direccion}</div>}
        {fiscal.telefono && <div>Telf.: {fiscal.telefono}</div>}
        {fiscal.rif && <div>RIF: {fiscal.rif}</div>}
      </div>

      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "64px" }}>
        {emisor.logo ? (
          // Alto fijo, como en el de siempre: la hoja se reparte midiendo.
          <img
            src={emisor.logo}
            alt=""
            style={{ display: "block", maxHeight: "64px", maxWidth: "200px", objectFit: "contain" }}
          />
        ) : (
          <div style={{ fontSize: "20px", fontWeight: 800, textAlign: "center", lineHeight: 1.15 }}>
            {emisor.negocio}
          </div>
        )}
      </div>

      <div style={{ ...texto, textAlign: "right" }}>
        <div style={ROTULO_CHICO}>Sucursal</div>
        {/* El nombre tal cual: con el rótulo encima, «Sucursal Norte» lo diría dos veces. */}
        <div style={{ fontSize: "12.5px", fontWeight: 700, color: TINTA }}>
          {emisor.sucursal || emisor.negocio}
        </div>
        {emisor.direccion && <div>{emisor.direccion}</div>}
        {emisor.telefono && <div>Telf.: {emisor.telefono}</div>}
      </div>
    </div>
  );
}

/**
 * Imprime las hojas de una `HojaCarta`: se abren en una pestaña aparte, como el
 * PDF de los rollos (ver `abrirHojasCarta`).
 */
export function imprimirHojaCarta(id: string): boolean {
  return abrirHojasCarta(id);
}

// =============================================================================
//  La plantilla
// =============================================================================

export function HojaCarta(props: HojaCartaProps) {
  const {
    id,
    titulo,
    documento,
    numero,
    fecha,
    emisor,
    datos,
    aviso,
    columnas,
    filas,
    resumen,
    totales,
    observaciones,
    aclaracion,
    despedida,
    firmas = [],
    leyenda = "Documento no fiscal",
    ampliable = true,
  } = props;

  // ── Las piezas ─────────────────────────────────────────────────────────────
  const fiscal = emisor.domicilioFiscal;
  const encabezado = (
    <div style={{ paddingBottom: "12px" }}>
      {fiscal ? (
        <EncabezadoDeSucursal emisor={emisor} fiscal={fiscal} />
      ) : (
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "24px" }}>
        <div style={{ minWidth: 0, maxWidth: "48%" }}>
          {emisor.logo ? (
            // Alto fijo: la hoja se reparte midiendo, y una imagen que todavía
            // no cargó mide cero.
            <div style={{ height: "64px", display: "flex", alignItems: "center" }}>
              <img
                src={emisor.logo}
                alt=""
                style={{ display: "block", maxHeight: "64px", maxWidth: "240px", objectFit: "contain" }}
              />
            </div>
          ) : (
            <div style={{ fontSize: "22px", fontWeight: 800, letterSpacing: "-0.01em", lineHeight: 1.15 }}>
              {emisor.negocio}
            </div>
          )}
        </div>
        <div style={{ textAlign: "right", color: TENUE, fontSize: "10.5px", lineHeight: 1.45 }}>
          {emisor.logo && (
            <div style={{ fontSize: "15px", fontWeight: 700, color: TINTA }}>{emisor.negocio}</div>
          )}
          {emisor.razonSocial && emisor.razonSocial !== emisor.negocio && <div>{emisor.razonSocial}</div>}
          {emisor.sucursal && (
            <div style={{ fontWeight: 700, color: TINTA, fontSize: emisor.logo ? "11px" : "13px" }}>
              {rotularSucursal(emisor.sucursal)}
            </div>
          )}
          {emisor.direccion && <div style={{ maxWidth: "300px", marginLeft: "auto" }}>{emisor.direccion}</div>}
          {emisor.telefono && <div>Telf.: {emisor.telefono}</div>}
          {emisor.rif && <div>RIF: {emisor.rif}</div>}
        </div>
      </div>
      )}

      {/* El nombre del documento, centrado: «GUÍA DE DESPACHO». */}
      {documento && (
        <div
          style={{
            marginTop: "14px",
            textAlign: "center",
            fontSize: "17px",
            fontWeight: 800,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
          }}
        >
          {documento}
        </div>
      )}

      {/* El recuadro: qué documento es y sus datos. */}
      <div style={{ marginTop: "12px", border: `1px solid ${LINEA_FUERTE}`, borderRadius: "3px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            gap: "16px",
            padding: "7px 10px",
            borderBottom: datos.length > 0 ? `1px solid ${LINEA}` : undefined,
          }}
        >
          <span style={{ fontSize: "12.5px", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase" }}>
            {titulo} <span style={{ letterSpacing: "0.04em" }}>N.° {numero}</span>
          </span>
          <span style={{ whiteSpace: "nowrap" }}>
            <span style={ROTULO}>Fecha: </span>
            {fecha}
          </span>
        </div>
        {datos.length > 0 && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              columnGap: "20px",
              rowGap: "4px",
              padding: "7px 10px",
            }}
          >
            {datos.map((d) => (
              <div key={d.etiqueta} style={{ minWidth: 0, gridColumn: d.completo ? "1 / -1" : undefined }}>
                <span style={ROTULO}>{d.etiqueta}: </span>
                {d.valor}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const avisoBloque = aviso ? <div style={{ paddingBottom: "10px" }}>{aviso}</div> : null;

  const colgroup = (
    <colgroup>
      {columnas.map((c) => (
        <col key={c.titulo} style={c.ancho ? { width: `${c.ancho}px` } : undefined} />
      ))}
    </colgroup>
  );

  const celda = (c: ColumnaDeHoja): CSSProperties => ({
    padding: "5px 8px",
    verticalAlign: "top",
    textAlign: c.derecha ? "right" : "left",
    fontVariantNumeric: c.derecha ? "tabular-nums" : undefined,
    whiteSpace: c.derecha ? "nowrap" : undefined,
    overflowWrap: "anywhere",
  });

  const thead = (
    <thead>
      <tr style={{ background: SOMBRA, printColorAdjust: "exact", WebkitPrintColorAdjust: "exact" } as CSSProperties}>
        {columnas.map((c) => (
          <th
            key={c.titulo}
            style={{ ...celda(c), ...ROTULO, color: TINTA, borderBottom: `1px solid ${LINEA_FUERTE}` }}
          >
            {c.titulo}
          </th>
        ))}
      </tr>
    </thead>
  );

  const fila = (f: FilaDeHoja) => (
    <tr key={f.id} style={{ borderBottom: `1px solid ${LINEA}` }}>
      {f.celdas.map((contenido, i) => (
        <td key={i} style={celda(columnas[i] ?? { titulo: "" })}>
          {contenido}
        </td>
      ))}
    </tr>
  );

  const tabla = (lista: FilaDeHoja[]) => (
    <table
      style={{
        width: "100%",
        borderCollapse: "collapse",
        tableLayout: "fixed",
        border: `1px solid ${LINEA_FUERTE}`,
      }}
    >
      {colgroup}
      {thead}
      <tbody>{lista.map(fila)}</tbody>
    </table>
  );

  const cierre = (
    <div style={{ paddingTop: "10px" }}>
      {/* Los totales. */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: "20px",
          border: `1px solid ${LINEA_FUERTE}`,
          borderRadius: "3px",
          padding: "7px 10px",
        }}
      >
        <div style={{ fontWeight: 700 }}>{resumen}</div>
        <div style={{ display: "grid", gridTemplateColumns: "auto auto", columnGap: "18px", rowGap: "2px" }}>
          {totales.map((t) => (
            <div key={t.etiqueta} style={{ display: "contents" }}>
              <span style={{ ...ROTULO, textAlign: "right", alignSelf: "center" }}>{t.etiqueta}:</span>
              <span
                style={{
                  textAlign: "right",
                  fontVariantNumeric: "tabular-nums",
                  fontWeight: t.fuerte ? 700 : 400,
                  fontSize: t.fuerte ? "13px" : undefined,
                }}
              >
                {t.valor}
              </span>
            </div>
          ))}
        </div>
      </div>

      {aclaracion && <div style={{ marginTop: "6px", color: TENUE, fontSize: "10px" }}>{aclaracion}</div>}

      {/* Las observaciones: siempre, aunque vacías, para escribir a mano. */}
      <div
        style={{
          marginTop: "8px",
          display: "flex",
          border: `1px solid ${LINEA_FUERTE}`,
          borderRadius: "3px",
          minHeight: "30px",
        }}
      >
        <div
          style={{
            ...ROTULO,
            color: TINTA,
            background: SOMBRA,
            printColorAdjust: "exact",
            WebkitPrintColorAdjust: "exact",
            padding: "7px 10px",
            borderRight: `1px solid ${LINEA}`,
            flexShrink: 0,
          } as CSSProperties}
        >
          Observaciones
        </div>
        <div style={{ padding: "6px 10px", minWidth: 0 }}>{observaciones || ""}</div>
      </div>

      {despedida && (
        <div style={{ marginTop: "14px", textAlign: "center", fontWeight: 700, fontSize: "12.5px" }}>
          {despedida}
        </div>
      )}
    </div>
  );

  const bloqueFirmas =
    firmas.length > 0 ? (
      <div style={{ display: "flex", gap: "32px", paddingTop: "56px", paddingBottom: "18px" }}>
        {firmas.map((f) => (
          <div key={f.etiqueta} style={{ flex: 1, textAlign: "center" }}>
            <div style={{ borderTop: `1px solid ${LINEA_FUERTE}`, paddingTop: "4px" }}>
              <div style={{ fontWeight: 700 }}>{f.etiqueta}</div>
              <div style={{ color: TENUE, fontSize: "10.5px" }}>{f.nombre || "Nombre, firma y cédula"}</div>
            </div>
          </div>
        ))}
      </div>
    ) : null;

  const pie = (n: number, de: number) => (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-end",
        gap: "16px",
        paddingTop: "8px",
        borderTop: `1px solid ${LINEA}`,
        fontSize: "10px",
        color: TENUE,
      }}
    >
      <span>
        softfi.online
        {de > 1 && ` · Página ${n} de ${de}`}
      </span>
      <span style={{ fontSize: "11px", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: TINTA }}>
        {leyenda}
      </span>
    </div>
  );

  // ── Medir y repartir ───────────────────────────────────────────────────────
  const medidor = useRef<HTMLDivElement>(null);
  const [hojas, setHojas] = useState<number[][] | null>(null);

  useLayoutEffect(() => {
    const m = medidor.current;
    if (!m) return;
    const alto = (sel: string) =>
      (m.querySelector(sel) as HTMLElement | null)?.getBoundingClientRect().height ?? 0;
    const altos = Array.from(m.querySelectorAll("[data-m=tabla] tbody > tr")).map(
      (tr) => tr.getBoundingClientRect().height,
    );
    const fijo = ALTO_UTIL - alto("[data-m=enc]") - alto("[data-m=tabla] thead") - alto("[data-m=pie]") - 2;
    const nuevas = repartir(altos, {
      primera: fijo - alto("[data-m=aviso]"),
      siguientes: fijo,
      final: alto("[data-m=cierre]") + alto("[data-m=firmas]"),
    });
    // Solo si cambió: volver a pintar lo mismo no hace falta.
    setHojas((antes) => (JSON.stringify(antes) === JSON.stringify(nuevas) ? antes : nuevas));
  });

  // ── A la medida de la pantalla ─────────────────────────────────────────────
  //
  // ⚠️ De ancho Y de alto (lo pidió el dueño, 2026-09-29): la hoja entera se ve
  // de una vez dentro de la ventana, sin desplazarse para ver el pie. En un
  // celular manda el ancho; en una computadora, casi siempre el alto.
  const caja = useRef<HTMLDivElement>(null);
  const [escala, setEscala] = useState(1);
  // «Hoja completa» de fábrica; «Ampliar» la lleva al ancho, para leer de cerca.
  const [ampliada, setAmpliada] = useState(false);
  useEffect(() => {
    const el = caja.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    // Lo que se desplaza por encima (el cuerpo de la ventana): su alto es lo
    // que se ve de una vez.
    let desplazable: HTMLElement | null = el.parentElement;
    while (desplazable && !/(auto|scroll)/.test(getComputedStyle(desplazable).overflowY)) {
      desplazable = desplazable.parentElement;
    }
    // ⚠️ El ancho es el de lo que CONTIENE al marco, no el del marco: el marco
    // abraza la hoja (sin un mar gris alrededor, que se veía incómodo), así
    // que su ancho depende de la escala y medirlo daría vueltas en círculo.
    const contenedor = el.parentElement ?? el;
    const medir = () => {
      const porAncho = (contenedor.clientWidth - 24) / HOJA_CARTA_PX.ancho;
      let porAlto = Infinity;
      if (desplazable) {
        const arriba =
          el.getBoundingClientRect().top -
          desplazable.getBoundingClientRect().top +
          desplazable.scrollTop;
        // El relleno del marco y, si está, la barrita de «Ampliar».
        const visible = desplazable.clientHeight - arriba - 24 - (ampliable ? 40 : 0);
        // Con muy poco alto (una pantalla acostada) se ignora: más chica que
        // eso ya no se leería.
        if (visible > 280 && !ampliada) porAlto = visible / HOJA_CARTA_PX.alto;
      }
      setEscala(Math.max(0.25, Math.min(1, porAncho, porAlto)));
    };
    medir();
    const obs = new ResizeObserver(medir);
    obs.observe(contenedor);
    if (desplazable) obs.observe(desplazable);
    window.addEventListener("resize", medir);
    return () => {
      obs.disconnect();
      window.removeEventListener("resize", medir);
    };
  }, [ampliada, ampliable]);

  const repartidas = hojas ?? [filas.map((_, i) => i)];
  // Lo que miden las hojas de verdad, una debajo de la otra: la vista reducida
  // ocupa eso por la escala.
  const altoDeLasHojas = repartidas.length * HOJA_CARTA_PX.alto + (repartidas.length - 1) * SEPARACION;

  return (
    <div
      ref={caja}
      className="mx-auto w-fit max-w-full overflow-x-hidden rounded-[var(--radius-control)] bg-surface-2 p-3"
    >
      {/* En su barrita, arriba: flotando sobre la hoja tapaba la esquina. */}
      {ampliable && (
        <div className="mb-2 flex justify-end">
          <button
            type="button"
            onClick={() => setAmpliada((a) => !a)}
            className="inline-flex items-center gap-1.5 rounded-full border border-line bg-surface px-3 py-1.5 text-xs font-medium text-ink-soft shadow-sm transition hover:border-accent hover:text-ink"
          >
            {ampliada ? <Minimize2 size={13} aria-hidden="true" /> : <Maximize2 size={13} aria-hidden="true" />}
            {ampliada ? "Ver la hoja completa" : "Ampliar"}
          </button>
        </div>
      )}
      {/* Donde se mide, fuera de la vista y a tamaño real. */}
      <div
        ref={medidor}
        aria-hidden="true"
        style={{
          ...LETRA,
          position: "fixed",
          left: "-10000px",
          top: 0,
          width: `${ANCHO_UTIL}px`,
          visibility: "hidden",
          pointerEvents: "none",
        }}
      >
        <div data-m="enc">{encabezado}</div>
        <div data-m="aviso">{avisoBloque}</div>
        <div data-m="tabla">{tabla(filas)}</div>
        <div data-m="cierre">{cierre}</div>
        <div data-m="firmas">{bloqueFirmas}</div>
        <div data-m="pie">{pie(1, 2)}</div>
      </div>

      {/*
        ⚠️ REDUCIDA CON `transform`, NO CON `zoom` (2026-10-19). Con `zoom`,
        Safari (el iPad y el iPhone) no achica la letra igual que la hoja: la
        dibuja más grande de lo que toca, los títulos de la tabla se montan
        («CAN / TIDA / D») y las filas se parten. Lo vieron en una capacitación
        con una tablet en vertical. `transform` achica el dibujo ya hecho, igual
        en todos los navegadores; como no achica el sitio que ocupa, el marco
        de fuera se queda con la medida reducida.
      */}
      <div
        style={{
          width: `${HOJA_CARTA_PX.ancho * escala}px`,
          height: `${altoDeLasHojas * escala}px`,
        }}
      >
        <div
          id={id}
          data-titulo={`${documento ?? titulo} N.° ${numero}`}
          style={{
            width: `${HOJA_CARTA_PX.ancho}px`,
            transform: `scale(${escala})`,
            transformOrigin: "0 0",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: `${SEPARACION}px`,
          }}
        >
          {repartidas.map((indices, n) => {
            const ultima = n === repartidas.length - 1;
            return (
              <div
                key={n}
                className={CLASE_HOJA_CARTA}
                style={{
                  ...LETRA,
                  boxSizing: "border-box",
                  width: `${HOJA_CARTA_PX.ancho}px`,
                  height: `${HOJA_CARTA_PX.alto}px`,
                  padding: `${MARGEN_Y}px ${MARGEN_X}px`,
                  position: "relative",
                  background: "#fff",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.25), 0 8px 24px rgba(0,0,0,0.18)",
                  display: "flex",
                  flexDirection: "column",
                  flexShrink: 0,
                }}
              >
                {encabezado}
                {n === 0 && avisoBloque}
                {indices.length > 0 && tabla(indices.map((i) => filas[i]!))}
                {ultima && cierre}
                {/* El hueco: el pie siempre abajo, aunque sea un producto. */}
                <div style={{ flex: 1 }} />
                {ultima && bloqueFirmas}
                {pie(n + 1, repartidas.length)}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
